import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiTrash2, FiZap, FiClock } from 'react-icons/fi';

function fromDatetimeLocalToIso(localStr) {
  if (!localStr || !String(localStr).trim()) return null;
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toTimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function recurrenceLabel(t) {
  const r = String(t.schedule_recurrence || '').trim().toLowerCase();
  if (r === 'once' && t.scheduled_at) return `Один раз: ${new Date(t.scheduled_at).toLocaleString()}`;
  const time = t.schedule_time || toTimeLocalValue(t.scheduled_at) || '—';
  const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  if (r === 'weekly') {
    const wd = t.schedule_weekday != null ? Number(t.schedule_weekday) : null;
    return `Раз в неделю: ${wd != null && !Number.isNaN(wd) ? days[wd] : '?'}, ${time}`;
  }
  if (r === 'weekdays') return `По будням, ${time}`;
  if (r === 'weekends') return `По выходным, ${time}`;
  if (t.scheduled_at) return `Следующий запуск: ${new Date(t.scheduled_at).toLocaleString()}`;
  return 'Без расписания';
}

function statusRu(s) {
  switch (s) {
    case 'pending':
      return 'Ожидает';
    case 'sent':
      return 'Выполнено';
    case 'failed':
      return 'Ошибка';
    default:
      return '—';
  }
}

const WEEKDAY_OPTIONS = [
  { v: 1, label: 'Понедельник' },
  { v: 2, label: 'Вторник' },
  { v: 3, label: 'Среда' },
  { v: 4, label: 'Четверг' },
  { v: 5, label: 'Пятница' },
  { v: 6, label: 'Суббота' },
  { v: 0, label: 'Воскресенье' },
];

export default function OnecMassShutdownScheduleModal({ open, onClose, isMobile, sidebarOffset = 320 }) {
  const [tab, setTab] = useState('run');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [runResult, setRunResult] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [linuxHint, setLinuxHint] = useState(false);
  /** @type {{ host: string, platform: string } | null} */
  const [serverContext, setServerContext] = useState(null);

  const [newJob, setNewJob] = useState({
    title: '',
    schedule_recurrence: 'none',
    schedule_weekday: 1,
    schedule_time: '09:00',
    scheduled_local: '',
  });

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const loadSchedules = useCallback(async () => {
    const r = await fetch('/api/admin/onec-shutdown/schedules', { headers: authHeaders() });
    if (!r.ok) return;
    const j = await r.json();
    setSchedules(Array.isArray(j.schedules) ? j.schedules : []);
  }, [authHeaders]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setRunResult(null);
    setServerContext(null);
    (async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const fetchShutdownContext = async () => {
        try {
          const r = await fetch('/api/admin/onec-shutdown/context', { headers });
          if (!r.ok) return;
          const j = await r.json().catch(() => ({}));
          if (j && typeof j.host === 'string' && typeof j.platform === 'string') {
            setServerContext({ host: j.host, platform: j.platform });
            if (j.platform !== 'win32') setLinuxHint(true);
          }
        } catch {
          /* noop */
        }
      };

      const fetchHostHint = async () => {
        try {
          const rh = await fetch('/api/admin/host', { headers });
          if (rh.ok) {
            const h = await rh.json().catch(() => ({}));
            if (h?.host && String(h.host).toLowerCase().includes('linux')) setLinuxHint(true);
          }
        } catch {
          /* noop */
        }
      };

      const fetchLocalUsersHint = async () => {
        try {
          const rl = await fetch('/api/admin/local-users', { headers });
          if (rl.status === 403 || rl.status === 500) {
            const t = await rl.text();
            if (t && (t.includes('Linux') || t.includes('Windows'))) setLinuxHint(true);
          }
        } catch {
          /* noop */
        }
      };

      await Promise.all([fetchShutdownContext(), fetchHostHint(), fetchLocalUsersHint(), loadSchedules()]);
      setLoading(false);
    })();
  }, [open, loadSchedules]);

  const runNow = async () => {
    if (!window.confirm('Завершить все сеансы 1С на сервере сейчас? (1cv8c / 1cv8)')) return;
    setSaving(true);
    setError('');
    setRunResult(null);
    try {
      const r = await fetch('/api/admin/shutdown-1c', { method: 'POST', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        throw new Error(data?.error || data?.message || 'Ошибка');
      }
      setRunResult(data);
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const createSchedule = async () => {
    setSaving(true);
    setError('');
    try {
      const onceIso = fromDatetimeLocalToIso(newJob.scheduled_local);
      let rec = newJob.schedule_recurrence || 'none';
      if (rec === 'none' && onceIso) rec = 'once';
      if (rec === 'once' && !onceIso) {
        throw new Error('Укажите дату и время или выберите тип расписания с временем');
      }
      if ((rec === 'weekly' || rec === 'weekdays' || rec === 'weekends') && !String(newJob.schedule_time || '').trim()) {
        throw new Error('Укажите время');
      }
      const body = {
        title: newJob.title || 'Завершение 1С',
        schedule_recurrence: rec,
        schedule_weekday: rec === 'weekly' ? Number(newJob.schedule_weekday) : null,
        schedule_time:
          rec === 'weekly' || rec === 'weekdays' || rec === 'weekends' ? newJob.schedule_time : null,
        scheduled_at: rec === 'once' ? onceIso : null,
      };
      const r = await fetch('/api/admin/onec-shutdown/schedules', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Не удалось создать');
      setNewJob({
        title: '',
        schedule_recurrence: 'none',
        schedule_weekday: 1,
        schedule_time: '09:00',
        scheduled_local: '',
      });
      await loadSchedules();
      setTab('list');
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const patchSchedule = async (id, body) => {
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`/api/admin/onec-shutdown/schedules/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Ошибка');
      await loadSchedules();
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Удалить это расписание?')) return;
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`/api/admin/onec-shutdown/schedules/${id}`, {
        method: 'DELETE',
        headers: { Authorization: authHeaders().Authorization },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || 'Ошибка');
      }
      await loadSchedules();
    } catch (e) {
      setError(e.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const deskOff = !isMobile ? Number(sidebarOffset) || 0 : 0;
  const overlayDesktop = !isMobile && deskOff > 0;

  const panelSurface =
    'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 18%, #f5f3ff 52%, #fff7ed 88%, #ffffff 100%)';

  /* Мобильные — полный экран; десктоп — область справа от сайдбара. Фон светлый, не «гробовая» тема */
  const overlay = isMobile
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh',
        minHeight: '-webkit-fill-available',
        zIndex: 650010,
        background: 'linear-gradient(160deg, rgba(14,165,233,0.12) 0%, rgba(99,102,241,0.1) 50%, rgba(251,146,60,0.12) 100%)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        padding: 0,
        boxSizing: 'border-box',
      }
    : {
        position: 'fixed',
        top: 0,
        left: overlayDesktop ? deskOff : 0,
        right: 0,
        bottom: 0,
        width: overlayDesktop ? `calc(100vw - ${deskOff}px)` : '100vw',
        height: '100vh',
        zIndex: 650010,
        background: overlayDesktop ? 'rgba(15, 23, 42, 0.38)' : 'rgba(15, 23, 42, 0.28)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        padding: 0,
        boxSizing: 'border-box',
      };

  const panel = {
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    maxHeight: 'none',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 0,
    background: panelSurface,
    border: 'none',
    boxShadow: isMobile ? 'none' : 'inset 0 1px 0 rgba(255,255,255,0.85)',
    color: '#0f172a',
    boxSizing: 'border-box',
  };

  const tabBtn = (active, label, id) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      style={{
        flex: 1,
        padding: '12px 8px',
        borderRadius: 12,
        border: active ? 'none' : '1px solid #e2e8f0',
        background: active
          ? 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 55%, #f97316 100%)'
          : 'rgba(255,255,255,0.85)',
        color: active ? '#ffffff' : '#475569',
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 15,
        boxShadow: active ? '0 6px 20px rgba(14, 165, 233, 0.35)' : '0 1px 3px rgba(15,23,42,0.06)',
      }}
    >
      {label}
    </button>
  );

  const node = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onec-mass-shutdown-title"
      style={overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={panel} onMouseDown={(e) => e.stopPropagation()}>
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile
              ? 'calc(12px + env(safe-area-inset-top, 0px)) 16px 14px 16px'
              : '18px 20px',
            background: 'linear-gradient(135deg, #0284c7 0%, #4f46e5 50%, #ea580c 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.22)',
            boxShadow: '0 10px 36px rgba(15, 23, 42, 0.18)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(224,242,254,0.95) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              }}
            >
              <FiClock size={24} />
            </div>
            <div>
              <div id="onec-mass-shutdown-title" style={{ fontWeight: 800, fontSize: isMobile ? 17 : 20, color: '#fff' }}>
                1С: завершение сеансов
              </div>
              <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.9)', marginTop: 4 }}>Сейчас или по расписанию</div>
            </div>
          </div>
          <button
            type="button"
            aria-label="Закрыть"
            onClick={onClose}
            style={{
              border: '2px solid rgba(255,255,255,0.4)',
              background: 'rgba(255,255,255,0.18)',
              color: '#fff',
              borderRadius: 12,
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <FiX size={22} />
          </button>
        </div>

        <div
          style={{
            flexShrink: 0,
            padding: isMobile ? '12px 14px' : '14px 18px',
            display: 'flex',
            gap: 8,
            background: 'rgba(255,255,255,0.55)',
            borderBottom: '1px solid rgba(148,163,184,0.2)',
            backdropFilter: 'blur(8px)',
          }}
        >
          {tabBtn(tab === 'run', 'Сейчас', 'run')}
          {tabBtn(tab === 'list', 'Расписания', 'list')}
          {tabBtn(tab === 'new', 'Новое', 'new')}
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: isMobile ? '0 16px calc(20px + env(safe-area-inset-bottom, 0px))' : '0 18px 20px',
            boxSizing: 'border-box',
          }}
        >
          {serverContext ? (
            <p
              style={{
                color: '#0f172a',
                fontSize: 15,
                margin: '0 0 12px',
                padding: '12px 14px',
                borderRadius: 12,
                background: 'rgba(241,245,249,0.95)',
                border: '1px solid #cbd5e1',
              }}
            >
              <strong>Узел API (здесь выполняется команда):</strong>{' '}
              <code style={{ color: '#0369a1' }}>{serverContext.host}</code>
              {' · '}
              {serverContext.platform === 'win32' ? 'Windows' : serverContext.platform}
              {serverContext.platform === 'win32'
                ? ' — завершаются процессы 1С именно на этой машине, не на других серверах.'
                : null}
              {serverContext.platform === 'win32' && serverContext.watchdogInstalled === false ? (
                <>
                  {' '}
                  <strong style={{ color: '#b45309' }}>
                    Watchdog SYSTEM не установлен — «Сейчас» может не снять чужие RDS-сеансы. От админа один раз:
                    server\scripts\install-onec-shutdown-watchdog.ps1
                  </strong>
                </>
              ) : null}
              {serverContext.watchdogInstalled ? (
                <> Watchdog SYSTEM активен — выброс во всех сессиях.</>
              ) : null}
            </p>
          ) : null}
          {linuxHint ? (
            <p
              style={{
                color: '#9a3412',
                fontSize: 15,
                margin: '0 0 12px',
                padding: '12px 14px',
                borderRadius: 12,
                background: 'linear-gradient(90deg, #ffedd5, #fef3c7)',
                border: '1px solid #fdba74',
              }}
            >
              Завершение процессов 1С (taskkill / PowerShell) доступно только если API запущен на Windows. На Linux или
              другом ОС кнопка и расписание не смогут снять сеансы на сервере 1С — нужен отдельный агент на Windows или
              перенос API на тот хост, где крутятся клиенты 1С.
            </p>
          ) : null}
          {error ? (
            <p
              style={{
                color: '#991b1b',
                fontSize: 15,
                margin: '0 0 12px',
                padding: '12px 14px',
                borderRadius: 12,
                background: '#fef2f2',
                border: '1px solid #fecaca',
              }}
            >
              {error}
            </p>
          ) : null}

          {tab === 'run' && (
            <div>
              <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.55, marginBottom: 16 }}>
                Немедленно завершает процессы <strong style={{ color: '#0f172a' }}>1cv8c.exe</strong> и{' '}
                <strong style={{ color: '#0f172a' }}>1cv8.exe</strong> только на том компьютере, где запущен этот API
                (см. «Узел API» выше). Тонкие клиенты на других ПК или на отдельном сервере 1С отсюда не закрываются.
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: isMobile ? 'stretch' : 'center',
                  width: '100%',
                }}
              >
                <button
                  type="button"
                  disabled={saving}
                  onClick={runNow}
                  style={{
                    width: isMobile ? '100%' : 'auto',
                    padding: isMobile ? '16px 20px' : '14px 32px',
                    borderRadius: isMobile ? 14 : 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 15,
                    cursor: saving ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isMobile ? 10 : 8,
                    boxShadow: '0 6px 22px rgba(234, 88, 12, 0.38)',
                    letterSpacing: isMobile ? 0 : '0.02em',
                    opacity: saving ? 0.65 : 1,
                  }}
                >
                  <FiZap size={isMobile ? 22 : 20} /> Выполнить сейчас
                </button>
              </div>
              {runResult ? (
                <div
                  style={{
                    marginTop: 16,
                    maxWidth: isMobile ? 'none' : 520,
                    marginLeft: isMobile ? 0 : 'auto',
                    marginRight: isMobile ? 0 : 'auto',
                    padding: 16,
                    borderRadius: 14,
                    background: runResult.success ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${runResult.success ? '#6ee7b7' : '#fecaca'}`,
                    fontSize: 15,
                    boxShadow: '0 4px 18px rgba(15,23,42,0.06)',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 6, color: runResult.success ? '#14532d' : '#991b1b' }}>
                    {runResult.message}
                  </div>
                  {runResult.processesBefore != null ? (
                    <div style={{ color: '#334155', fontSize: 15 }}>
                      Процессов клиента 1С до команды: <strong>{runResult.processesBefore}</strong>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}

          {tab === 'list' && (
            <div>
              <p
                style={{
                  margin: '0 0 14px',
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: '#64748b',
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'rgba(241,245,249,0.9)',
                  border: '1px solid #e2e8f0',
                }}
              >
                Общие расписания: их видят все, у кого есть право завершать сеансы 1С (администратор, HR, директор).
                Любой из них может <strong>выключить</strong> или <strong>удалить</strong> любое расписание — в том числе созданное другим.
              </p>
              {loading ? (
                <p style={{ color: '#64748b', fontSize: 15 }}>Загрузка…</p>
              ) : schedules.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: 15 }}>Нет расписаний. Вкладка «Новое».</p>
              ) : (
                schedules.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      marginBottom: 12,
                      padding: 16,
                      borderRadius: 14,
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(15,23,42,0.07)',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{s.title || `Задача #${s.id}`}</div>
                    <div style={{ fontSize: 14, color: '#94a3b8', marginTop: 4 }}>
                      Создал:{' '}
                      <span style={{ color: '#475569' }}>
                        {s.owner_username || (s.owner_user_id != null ? `пользователь #${s.owner_user_id}` : '—')}
                      </span>
                    </div>
                    <div style={{ fontSize: 15, color: '#64748b', marginTop: 4 }}>{recurrenceLabel(s)}</div>
                    <div style={{ fontSize: 15, marginTop: 6, color: '#334155' }}>
                      Статус: <strong>{statusRu(s.schedule_status)}</strong>
                      {s.enabled === 0 ? ' · выкл' : ''}
                    </div>
                    {s.last_run_at ? (
                      <div style={{ fontSize: 15, color: '#15803d', marginTop: 4 }}>
                        Последний запуск: {new Date(s.last_run_at).toLocaleString()}
                        {s.last_processes_before != null ? ` · процессов было: ${s.last_processes_before}` : ''}
                      </div>
                    ) : null}
                    {s.last_error ? (
                      <div style={{ fontSize: 15, color: '#b91c1c', marginTop: 6 }}>{s.last_error}</div>
                    ) : null}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => patchSchedule(s.id, { enabled: Number(s.enabled) !== 1 })}
                        style={{
                          padding: '12px 18px',
                          borderRadius: 10,
                          border: '1px solid #cbd5e1',
                          background: '#f8fafc',
                          color: '#334155',
                          cursor: 'pointer',
                          fontSize: 15,
                          fontWeight: 600,
                        }}
                      >
                        {s.enabled === 0 ? 'Включить' : 'Выключить'}
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => deleteSchedule(s.id)}
                        style={{
                          padding: '12px 18px',
                          borderRadius: 10,
                          border: '1px solid #fecaca',
                          background: '#fef2f2',
                          color: '#b91c1c',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 15,
                          fontWeight: 600,
                        }}
                      >
                        <FiTrash2 size={16} /> Удалить
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === 'new' && (
            <div>
              <label style={{ display: 'block', fontSize: 15, fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                Название
              </label>
              <input
                value={newJob.title}
                onChange={(e) => setNewJob((p) => ({ ...p, title: e.target.value }))}
                placeholder="Напр. Конец смены"
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 10,
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#0f172a',
                  marginBottom: 14,
                  boxSizing: 'border-box',
                  fontSize: 16,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                }}
              />
              <label style={{ display: 'block', fontSize: 15, fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                Расписание
              </label>
              <select
                value={newJob.schedule_recurrence}
                onChange={(e) => setNewJob((p) => ({ ...p, schedule_recurrence: e.target.value }))}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 10,
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#0f172a',
                  marginBottom: 12,
                  fontSize: 16,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                }}
              >
                <option value="none">Без расписания (не создавать)</option>
                <option value="once">Один раз (дата и время)</option>
                <option value="weekly">Раз в неделю</option>
                <option value="weekdays">По будням</option>
                <option value="weekends">По выходным</option>
              </select>
              {newJob.schedule_recurrence === 'once' ? (
                <input
                  type="datetime-local"
                  value={newJob.scheduled_local}
                  onChange={(e) => setNewJob((p) => ({ ...p, scheduled_local: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#0f172a',
                    marginBottom: 12,
                    boxSizing: 'border-box',
                    fontSize: 16,
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                  }}
                />
              ) : null}
              {newJob.schedule_recurrence === 'weekly' ? (
                <select
                  value={newJob.schedule_weekday}
                  onChange={(e) => setNewJob((p) => ({ ...p, schedule_weekday: Number(e.target.value) }))}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#0f172a',
                    marginBottom: 12,
                    fontSize: 16,
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                  }}
                >
                  {WEEKDAY_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {['weekly', 'weekdays', 'weekends'].includes(newJob.schedule_recurrence) ? (
                <input
                  type="time"
                  value={newJob.schedule_time}
                  onChange={(e) => setNewJob((p) => ({ ...p, schedule_time: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#0f172a',
                    marginBottom: 14,
                    boxSizing: 'border-box',
                    fontSize: 16,
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                  }}
                />
              ) : null}
              <div
                style={{
                  display: 'flex',
                  justifyContent: isMobile ? 'stretch' : 'flex-end',
                  marginTop: 2,
                }}
              >
                <button
                  type="button"
                  disabled={saving || newJob.schedule_recurrence === 'none'}
                  onClick={createSchedule}
                  style={{
                    width: isMobile ? '100%' : 'auto',
                    padding: isMobile ? '14px 20px' : '12px 22px',
                    borderRadius: 12,
                    border: 'none',
                    background: 'linear-gradient(135deg, #0284c7 0%, #6366f1 100%)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: saving ? 'wait' : 'pointer',
                    boxShadow: '0 6px 22px rgba(37, 99, 235, 0.35)',
                    opacity: saving || newJob.schedule_recurrence === 'none' ? 0.55 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Сохранить расписание
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}
